import { useEffect, type ReactElement } from 'react';
import { BspProvider } from '@eventbrite/bsp-sdk/react';

import { useBspConfig } from '@/src/listings/bsp/config';
import { Wrapper } from '@/src/listings/components/_shared/Wrapper';
import { QueryClientProvider } from '@/src/listings/providers/QueryClientProvider';
import { IntlProviderWrapper } from '@/src/listings/providers/IntlProviders';
import { SurfaceConsentScripts } from '@/shared/consent/SurfaceConsentScripts';
import { useOverrideSession } from '@/src/listings/hooks/useOverrideSession';
import {
  setOverrideSessionEnabled,
  usePageSourceOverrideKeepalive,
} from '@/lib/release/pageSourceOverride';
import '@/src/listings/styles/listingsGlobal.module.scss';

export interface ListingsLayoutServerProps {
  stage?: string;
  serverUrl?: string;
  locale?: string;
  messages?: Record<string, string>;
  [key: string]: unknown;
}

export function ListingsLayout({ page }: { page: ReactElement; route: string }) {
  const props = page.props as ListingsLayoutServerProps;
  const { stage, serverUrl, locale = 'en-US', messages = {} } = props;
  const bspConfig = useBspConfig();
  const hasOverrideSession = useOverrideSession();

  usePageSourceOverrideKeepalive(hasOverrideSession);
  useEffect(() => {
    setOverrideSessionEnabled(Boolean(hasOverrideSession));
  }, [hasOverrideSession]);

  return (
    <>
      <BspProvider config={bspConfig}>
        <QueryClientProvider>
          <IntlProviderWrapper messages={messages} locale={locale}>
            <Wrapper serverUrl={serverUrl ?? ''} stage={stage ?? ''} locale={locale}>
              {page}
            </Wrapper>
          </IntlProviderWrapper>
        </QueryClientProvider>
      </BspProvider>
      <SurfaceConsentScripts />
    </>
  );
}

export default ListingsLayout;
